import { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronRightIcon, GripVerticalIcon, MenuIcon, PlusIcon } from 'lucide-react';
import { Reorder, useDragControls } from 'motion/react';
import SelectIconClub, { findLucideMenuIcon } from '@/components/SelectIconClub';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogBody,
    DialogTitle,
    ScrollDialogContent,
    ScrollDialogFooter,
    ScrollDialogHeader,
} from '@/components/ui/dialog-scroll';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useFetch } from '@/hooks/useFecth';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import ClubEmptyList from './ClubEmptyList';
import ClubLinkDestinationFields from './ClubLinkDestinationFields';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import { useClubConfirm } from '../providers/ClubConfirmProvider';

const menuSchema = z.object({
    icon: z.string().optional(),
    label: z.string().min(1, 'Informe o título'),
    type: z.string().min(1),
    link: z.string().optional(),
    isExternal: z.boolean(),
    onWeb: z.boolean(),
    onMobile: z.boolean(),
    onDrawer: z.boolean(),
    onCarousel: z.boolean(),
    onHub: z.boolean(),
    description: z.string().optional(),
    status: z.boolean(),
});

function MenuForm({ data, mutate, open, onOpenChange, loading = false }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const confirm = useClubConfirm();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(menuSchema),
        defaultValues: {
            icon: '',
            label: '',
            type: 'link',
            link: 'https://',
            isExternal: false,
            onWeb: false,
            onMobile: false,
            onDrawer: false,
            onCarousel: false,
            onHub: false,
            description: '',
            status: true,
        },
    });

    useEffect(() => {
        if (!open) return;

        if (data) {
            form.reset({
                icon: data.icon || '',
                label: data.label || '',
                type: data.type || 'link',
                link: data.link || 'https://',
                isExternal: Boolean(data.isExternal),
                onWeb: Boolean(data.onWeb),
                onMobile: Boolean(data.onMobile),
                onDrawer: Boolean(data.onDrawer),
                onCarousel: Boolean(data.onCarousel),
                onHub: Boolean(data.onHub),
                description: data.description || '',
                status: data.status !== false,
            });
        } else {
            form.reset({
                icon: '',
                label: '',
                type: 'link',
                link: 'https://',
                isExternal: false,
                onWeb: false,
                onMobile: false,
                onDrawer: false,
                onCarousel: false,
                onHub: false,
                description: '',
                status: true,
            });
        }
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            if (!data?.id) {
                await server.post('club/settings/menu', values);
                enqueueSnackbar('Item adicionado');
            } else {
                await server.put(`club/settings/menu/${data.id}`, values);
                enqueueSnackbar('Item alterado');
            }

            await mutate();
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const onDelete = async () => {
        if (!data?.id) return;

        try {
            await confirm({
                title: 'Remover item do menu',
                description: 'Você confirma que está removendo o item?',
                confirmationText: 'Excluir',
                confirmationButtonProps: { variant: 'destructive' },
            });

            await server.delete(`club/settings/menu/${data.id}`);
            enqueueSnackbar('Item removido');
            await mutate();
            onOpenChange(false);
        } catch (error) {
            if (error?.message === 'cancelled') return;
            setErrors(error);
        }
    };

    const { isSubmitting } = form.formState;
    const onMobile = form.watch('onMobile');
    const onHub = form.watch('onHub');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <ScrollDialogContent container={portalContainer} className="sm:max-w-lg">
                <ScrollDialogHeader>
                    <DialogTitle>{data?.id ? 'Editar item do menu' : 'Adicionar item do menu'}</DialogTitle>
                </ScrollDialogHeader>

                {loading ? (
                    <DialogBody>
                        <div className="flex justify-center py-10">
                            <Spinner className="size-6" />
                        </div>
                    </DialogBody>
                ) : (
                    <form onSubmit={form.handleSubmit(onSubmit)} className="contents">
                        <DialogBody>
                            <FieldGroup>
                                <SelectIconClub
                                    onChange={form.setValue}
                                    name="icon"
                                    value={form.watch('icon')}
                                >
                                    <Field>
                                        <FieldLabel htmlFor="menu-label">Label (título)</FieldLabel>
                                        <Controller
                                            name="label"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Input id="menu-label" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                    </Field>
                                </SelectIconClub>

                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Destino do menu</CardTitle>
                                        <CardDescription>Crie um link nos menus do seu clube e defina o destino.</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <ClubLinkDestinationFields
                                            control={form.control}
                                            setValue={form.setValue}
                                            watch={form.watch}
                                        />
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Para clubes na web</CardTitle>
                                        <CardDescription>Ele aparece no site?</CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Field orientation="horizontal">
                                            <FieldLabel htmlFor="menu-on-web">Habilitado</FieldLabel>
                                            <Controller
                                                name="onWeb"
                                                control={form.control}
                                                render={({ field }) => (
                                                    <Switch id="menu-on-web" checked={field.value} onCheckedChange={field.onChange} />
                                                )}
                                            />
                                        </Field>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Para aplicativos</CardTitle>
                                        <CardDescription>Como ele aparece no app?</CardDescription>
                                    </CardHeader>
                                    <CardContent className="flex flex-col gap-4">
                                        <Field orientation="horizontal">
                                            <FieldLabel htmlFor="menu-on-mobile">Habilitado</FieldLabel>
                                            <Controller
                                                name="onMobile"
                                                control={form.control}
                                                render={({ field }) => (
                                                    <Switch
                                                        id="menu-on-mobile"
                                                        checked={field.value}
                                                        onCheckedChange={field.onChange}
                                                    />
                                                )}
                                            />
                                        </Field>

                                        {onMobile ? (
                                            <>
                                                <Separator />
                                                <Field orientation="horizontal">
                                                    <div className="flex flex-1 flex-col gap-1">
                                                        <FieldLabel htmlFor="menu-external">Abrir fora do app?</FieldLabel>
                                                        <FieldDescription>
                                                            Essa opção abre o link no navegador fora do aplicativo.
                                                        </FieldDescription>
                                                    </div>
                                                    <Controller
                                                        name="isExternal"
                                                        control={form.control}
                                                        render={({ field }) => (
                                                            <Switch
                                                                id="menu-external"
                                                                checked={field.value}
                                                                onCheckedChange={field.onChange}
                                                            />
                                                        )}
                                                    />
                                                </Field>
                                            </>
                                        ) : null}
                                    </CardContent>
                                </Card>

                                <Field orientation="horizontal">
                                    <div className="flex flex-1 flex-col gap-1">
                                        <FieldLabel htmlFor="menu-drawer">No menu?</FieldLabel>
                                        <FieldDescription>Menu superior no site e tela Menu no app.</FieldDescription>
                                    </div>
                                    <Controller
                                        name="onDrawer"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Switch id="menu-drawer" checked={field.value} onCheckedChange={field.onChange} />
                                        )}
                                    />
                                </Field>

                                <Field orientation="horizontal">
                                    <div className="flex flex-1 flex-col gap-1">
                                        <FieldLabel htmlFor="menu-carousel">No acesso rápido?</FieldLabel>
                                        <FieldDescription>Bloco Acesso rápido na home do site e do app.</FieldDescription>
                                    </div>
                                    <Controller
                                        name="onCarousel"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Switch id="menu-carousel" checked={field.value} onCheckedChange={field.onChange} />
                                        )}
                                    />
                                </Field>

                                <Field orientation="horizontal">
                                    <div className="flex flex-1 flex-col gap-1">
                                        <FieldLabel htmlFor="menu-hub">No hub de benefícios?</FieldLabel>
                                        <FieldDescription>Card no Hub de benefícios da home do site e do app.</FieldDescription>
                                    </div>
                                    <Controller
                                        name="onHub"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Switch id="menu-hub" checked={field.value} onCheckedChange={field.onChange} />
                                        )}
                                    />
                                </Field>

                                {onHub ? (
                                    <Field>
                                        <FieldLabel htmlFor="menu-description">Frase do hub</FieldLabel>
                                        <Controller
                                            name="description"
                                            control={form.control}
                                            render={({ field }) => (
                                                <Textarea
                                                    id="menu-description"
                                                    rows={2}
                                                    {...field}
                                                    value={field.value ?? ''}
                                                />
                                            )}
                                        />
                                        <FieldDescription>Texto curto exibido no card do hub.</FieldDescription>
                                    </Field>
                                ) : null}

                                <Field orientation="horizontal">
                                    <FieldLabel htmlFor="menu-status">Habilitado</FieldLabel>
                                    <Controller
                                        name="status"
                                        control={form.control}
                                        render={({ field }) => (
                                            <Switch id="menu-status" checked={field.value} onCheckedChange={field.onChange} />
                                        )}
                                    />
                                </Field>
                            </FieldGroup>
                        </DialogBody>

                        <ScrollDialogFooter>
                            {data?.id ? (
                                <Button type="button" variant="destructive" onClick={onDelete} disabled={isSubmitting}>
                                    Excluir
                                </Button>
                            ) : null}
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                            </Button>
                        </ScrollDialogFooter>
                    </form>
                )}
            </ScrollDialogContent>
        </Dialog>
    );
}

function DialogMenuAdd({ mutate }) {
    const { id, open, handleClose } = useDialog('#menu');
    const { data, isFetching } = useFetch(open && id ? `/club/settings/menu/${id}` : null);

    if (!open) return null;

    return (
        <MenuForm
            data={id ? data : undefined}
            mutate={mutate}
            open={open}
            loading={Boolean(id && isFetching)}
            onOpenChange={isOpen => !isOpen && handleClose()}
        />
    );
}

function ListMenuItem({ item, onDragEnd }) {
    const controls = useDragControls();
    const menuIcon = findLucideMenuIcon(item.icon);
    const ItemIcon = menuIcon?.Component;

    return (
        <Reorder.Item
            value={item.id}
            dragListener={false}
            dragControls={controls}
            onDragEnd={onDragEnd}
            className="flex items-center gap-2 border-b bg-background p-3 last:border-b-0"
        >
            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="cursor-grab active:cursor-grabbing"
                onPointerDown={event => controls.start(event)}
            >
                <GripVerticalIcon className="size-4" />
            </Button>
            {ItemIcon ? (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-muted/40">
                    <ItemIcon className="size-4" />
                </div>
            ) : null}
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.label}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant="outline">#{item.id}</Badge>
                    {item.onMobile ? <Badge variant="secondary">App</Badge> : null}
                    {item.onWeb ? <Badge variant="secondary">Web</Badge> : null}
                    {item.onDrawer ? <Badge variant="secondary">Menu</Badge> : null}
                    {item.onCarousel ? <Badge variant="secondary">Acesso rápido</Badge> : null}
                    {item.onHub ? <Badge variant="secondary">Hub</Badge> : null}
                </div>
            </div>
            <Button variant="ghost" size="icon-sm" render={<Link to={`#menu|${item.id}`} title="Abrir" />}>
                <ChevronRightIcon className="size-4" />
            </Button>
        </Reorder.Item>
    );
}

function ListMenu({ data, mutate, onReorderSave }) {
    const handleReorder = useCallback(
        newOrder => {
            const reordered = newOrder
                .map((id, index) => {
                    const item = data?.find(entry => entry.id === id);
                    return item ? { ...item, position: index } : null;
                })
                .filter(Boolean);
            mutate({ data: reordered }, false);
        },
        [data, mutate],
    );

    const handleDragEnd = useCallback(() => {
        onReorderSave(data?.map(item => item.id));
    }, [data, onReorderSave]);

    if (!data?.length) {
        return <ClubEmptyList />;
    }

    return (
        <Reorder.Group axis="y" values={data.map(item => item.id)} onReorder={handleReorder} className="flex flex-col">
            {data.map(item => (
                <ListMenuItem key={item.id} item={item} onDragEnd={handleDragEnd} />
            ))}
        </Reorder.Group>
    );
}

export default function MenuClubCard() {
    const { data, mutate } = useFetch('/club/settings/menu');
    const { enqueueSnackbar } = useClubErrors();
    const { open: manageOpen, handleClose: closeManage } = useDialog('#menu-manage');
    const portalContainer = useClubPortalContainer();
    const count = data?.length ?? 0;
    const activeCount = data?.filter(item => item.status !== false).length ?? 0;

    async function handleSendReorder(reordered) {
        await server.post('/club/settings/menu', { reorder: reordered });
        enqueueSnackbar('Itens reordenados');
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={MenuIcon}
                        title="Menu de navegação"
                        description="Defina menus personalizados para o seu clube."
                    />
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                    <span className="text-muted-foreground">Itens cadastrados</span>
                    <p className="font-medium">
                        {count === 0 ? 'Nenhum item' : `${count} ite${count !== 1 ? 'ns' : 'm'}`}
                    </p>
                    {count > 0 ? <p className="text-muted-foreground">{activeCount} ativo(s)</p> : null}
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#menu-manage" />}>
                        Gerenciar menu
                    </Button>
                </CardFooter>
            </Card>

            <Dialog open={manageOpen} onOpenChange={isOpen => !isOpen && closeManage()}>
                <ScrollDialogContent container={portalContainer} className="sm:max-w-2xl">
                    <ScrollDialogHeader>
                        <DialogTitle>Menu de navegação</DialogTitle>
                    </ScrollDialogHeader>
                    <DialogBody>
                        <div className="mb-4 flex justify-end">
                            <Button variant="outline" size="sm" render={<Link to="#menu" />}>
                                <PlusIcon className="size-4" />
                                Adicionar item
                            </Button>
                        </div>
                        <Card>
                            <CardContent className="p-0">
                                <ListMenu data={data || []} mutate={mutate} onReorderSave={handleSendReorder} />
                            </CardContent>
                        </Card>
                    </DialogBody>
                    <ScrollDialogFooter>
                        <Button variant="outline" onClick={closeManage}>
                            Fechar
                        </Button>
                    </ScrollDialogFooter>
                </ScrollDialogContent>
            </Dialog>

            <DialogMenuAdd mutate={mutate} />
        </>
    );
}
